import styles from './guestinfo.module.scss';
import { GuestInfoProps } from '@msc-b2c/interfaces/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { BaseSelect, InputText, Text } from '@msc-b2c/shared-components';
import {
  capitalizeFirstLetterAndLowerCaseRest,
  getActiveSession,
  handleTabListKeyDown,
  replacePlaceholders,
  resolveDateFormat,
  useGetBookingQuery,
} from '@msc-b2c/shared-utils';
import { useForm } from 'react-hook-form';
import dayjs from 'dayjs';

export function GuestInfo(props: Readonly<GuestInfoProps>) {
  const { title, guestTitle, formLabels, countryOfResidenceMappingList } = props;
  const { register, clearErrors } = useForm();

  const {
    countryOfResidenceLabel,
    dateOfBirthLabel,
    emailLabel,
    firstNameLabel,
    genderLabel,
    lastNameLabel,
    mscVoyagersClubLabel,
    phoneLabel,
  } = formLabels;

  const { bookingNumber, cruiseId, passengerId } = getActiveSession();
  const { data: booking } = useGetBookingQuery(
    bookingNumber && bookingNumber !== '' ? bookingNumber : skipToken
  );

  const tabsRef = useRef<HTMLDivElement>(null);
  const [fadeLeft, setFadeLeft] = useState(false);
  const [fadeRight, setFadeRight] = useState(false);

  // check scroll to apply fade effect
  const checkScrollAndApplyFade = () => {
    const tabsRefElement = tabsRef.current;
    if (!tabsRefElement) return;
    setFadeLeft(tabsRefElement.scrollLeft > 0);
    setFadeRight(
      tabsRefElement.scrollLeft + tabsRefElement.clientWidth < tabsRefElement.scrollWidth
    );
  };

  useEffect(() => {
    const tabsRefElement = tabsRef.current;
    if (!tabsRefElement) return;
    checkScrollAndApplyFade(); // initial check
    tabsRefElement.addEventListener('scroll', checkScrollAndApplyFade);
    return () => tabsRefElement.removeEventListener('scroll', checkScrollAndApplyFade);
  }, []);

  const passengers = useMemo(() => {
    const cabinsBooked = booking?.cabins || [];
    const activePassengerCruiseCabinsBooked = cabinsBooked.filter(
      (cabin) => cabin.cruise.id === cruiseId && cabin.passengers?.some((p) => p.id === passengerId)
    );
    const passengerIds = activePassengerCruiseCabinsBooked.flatMap(
      (cabin) => cabin.passengers?.map((p) => p.id) ?? []
    );
    return (booking?.passengers ?? []).filter((p) => passengerIds.includes(p.id));
  }, [booking, cruiseId, passengerId]);

  const [activeIndex, setActiveIndex] = useState(0);
  const active = passengers[activeIndex];

  const formattedDateOfBirth = active?.dateOfBirth
    ? dayjs(active.dateOfBirth).format(resolveDateFormat())
    : '';

  const countryItems = useMemo(
    () =>
      (countryOfResidenceMappingList ?? []).map((countryMapping) => ({
        value: countryMapping.value ?? '',
        text: countryMapping['jcr:title'] ?? countryMapping.value ?? '',
      })),
    [countryOfResidenceMappingList]
  );

  const renderField = (index: number, name: string, label: string, value?: string) => (
    <div className={styles['guest-info__field']}>
      <InputText
        index={index}
        name={name}
        label={label}
        register={register}
        placeholder={value || ''}
        disabled
      />
    </div>
  );

  const renderSelectField = (fieldName: string, label: string, value?: string) => (
    <div className={styles['guest-info__field']}>
      <BaseSelect
        label={label}
        placeholder={label}
        ariaLabel={label}
        fieldName={fieldName}
        items={countryItems}
        value={value ?? ''}
        disabled
        errors={{}}
        clearErrors={clearErrors}
        onChange={() => undefined}
      />
    </div>
  );

  const renderSkeleton = () => (
    <div className={`${styles['guest-info__wrapper']} guest-info-skeleton`}>
      <div className={styles['guest-info__container']}>
        <div className={styles['guest-info__title']}>
          <div className="skeleton skeleton-title" />
        </div>
        <div className={styles['guest-info__tabs']}>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="skeleton skeleton-tab"
            />
          ))}
        </div>
        <div className={styles['guest-info__form']}>
          {[1, 2].map((row) => (
            <div
              key={row}
              className={styles['guest-info__row']}
            >
              <div className="skeleton skeleton-field" />
              <div className="skeleton skeleton-field" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderContent = () => (
    <div className={styles['guest-info__wrapper']}>
      <div className={styles['guest-info__container']}>
        <div className={styles['guest-info__title']}>
          <Text
            text={title}
            tagHtml="span"
            styleOption="header-s"
          />
        </div>
        <div
          className={`${styles['guest-info__tabs-wrapper']} ${
            fadeLeft ? styles['guest-info__tabs-wrapper--fade-left'] : ''
          } ${fadeRight ? styles['guest-info__tabs-wrapper--fade-right'] : ''}
        `}
        >
          <div
            className={`${styles['guest-info__tabs']}`}
            ref={tabsRef}
            role="tablist"
            aria-label={title}
          >
            {passengers.map((passenger, idx) => (
              <button
                key={passenger.id}
                type="button"
                role="tab"
                id={`guest-info-tab-${idx}`}
                aria-selected={activeIndex === idx}
                aria-controls="guest-info-panel"
                className={`${styles['guest-info__tab']} ${
                  activeIndex === idx ? styles['guest-info__tab-active'] : ''
                }`}
                onClick={() => setActiveIndex(idx)}
                onKeyDown={(event) =>
                  handleTabListKeyDown(event, {
                    tabCount: passengers.length,
                    currentIndex: idx,
                    onSelectTab: setActiveIndex,
                    focusTab: (newIndex) => {
                      (
                        tabsRef.current?.children[newIndex] as HTMLButtonElement | undefined
                      )?.focus();
                    },
                  })
                }
              >
                {replacePlaceholders(guestTitle, {
                  number: `${idx + 1}`,
                })}
              </button>
            ))}
          </div>
        </div>

        <div
          className={styles['guest-info__form']}
          role="tabpanel"
          id="guest-info-panel"
          aria-labelledby={`guest-info-tab-${activeIndex}`}
        >
          <div className={styles['guest-info__row']}>
            {renderField(1, firstNameLabel, firstNameLabel, active.name)}
            {renderField(2, lastNameLabel, lastNameLabel, active.surname)}
          </div>

          <div className={styles['guest-info__row']}>
            {renderField(3, dateOfBirthLabel, dateOfBirthLabel, formattedDateOfBirth)}
            {renderField(
              4,
              genderLabel,
              genderLabel,
              capitalizeFirstLetterAndLowerCaseRest(active.gender ?? '')
            )}
          </div>

          <div className={styles['guest-info__row']}>
            {renderSelectField(
              countryOfResidenceLabel,
              countryOfResidenceLabel,
              active.address?.country
            )}
            {activeIndex === 0 && renderField(6, emailLabel, emailLabel, active.email)}
          </div>

          {activeIndex === 0 && (
            <div className={styles['guest-info__row']}>
              {renderField(7, phoneLabel, phoneLabel, active.phone)}
              {renderField(
                8,
                mscVoyagersClubLabel,
                mscVoyagersClubLabel,
                active.membership?.number
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return passengers.length > 0 ? renderContent() : renderSkeleton();
}
